import { useEffect, useMemo, useRef, useState } from "react";

import { nanoid } from "@reduxjs/toolkit";
import { EntityId } from "@reduxjs/toolkit";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";

import { Property } from "csstype";

import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import RefreshIcon from "@mui/icons-material/Refresh";
import WifiOff from "@mui/icons-material/WifiOff";
import VideocamOffIcon from "@mui/icons-material/VideocamOff";

import { useAppDispatch, useAppSelector } from "app/hooks";
import { selectSiteById } from "features/sites/store/selectors";
import { selectDeviceById } from "features/devices/store/selectors";
import { coreActions } from "features/core/store/slice";
import { selectInWindowMode } from "features/core/store/selectors";
import { userSelectors } from "features/user/store/selectors";
import { liveStreamingActions } from "./store/slice";

import { useCreateLiveStreamMutation } from "services/streamingApi/streamingPodManager.api";

import { PlayerCard, PlayerCardRef } from "features/videoPlayer/PlayerCard";
import { CloseButton } from "features/videoPlayer/CloseButton";
import { StreamPopOver } from "./StreamPopOver";
import { CreateLiveStreamRequest } from "types/CreateLiveStreamRequest";
import { StreamQualityType } from "types/StreamQualityType";
import { OpenInNewWindowButton } from "./OpenInNewWindowButton";
import { HlsPlayer } from "features/videoPlayer/HlsPlayer";
import { PlayerCardStatus } from "features/videoPlayer/PlayerCardStatus";
import { PlayerRef } from "features/videoPlayer/types/PlayerRef";
import { useSignalRGroupSubscriber } from "features/messages/hooks/useSignalRGroup";
import { OpenDeviceDiagnosticsButton } from "./OpenDeviceDiagnosticsButton";
import { FeatureFlagGuard } from "features/appFeatures/FeatureFlagGuard";
import { AppFeature } from "features/appFeatures/types/AppFeature";
import { StreamPlayerModeType } from "types/StreamPlayerModeType";
import { useAppInsightsContext } from "@microsoft/applicationinsights-react-js";
import { LiveStreamFeedTypeButton } from "./LiveStreamFeedTypeButton";
import { LiveStreamFeedType } from "./types/LiveStreamFeedType";
import { liveStreamingSelectors } from "./store/selectors";

export interface LiveStreamPlayerProps {
    siteId: EntityId;
    deviceId: EntityId;
    streamQuality: StreamQualityType | null;
    enableAudio: boolean;
    audioEnabledByDefault: boolean;
    defaultLiveStreamFeedType: LiveStreamFeedType;
}

export function LiveStreamPlayer(props: LiveStreamPlayerProps) {
    const dispatch = useAppDispatch();
    const inWindowMode = useAppSelector(selectInWindowMode);
    const [hlsUrl, setHlsUrl] = useState<string>();
    const userSessionId = useAppSelector(userSelectors.selectSessionId);
    const [liveStreamCorrelationId, setLiveStreamCorrelationId] =
        useState<string>(`${userSessionId}|${nanoid()}`);
    const [playerMode, setPlayerMode] = useState<StreamPlayerModeType>(
        StreamPlayerModeType.LiveStream,
    );
    const [liveStreamFeedType, setLiveStreamFeedType] =
        useState<LiveStreamFeedType>(props.defaultLiveStreamFeedType);

    const appInsights = useAppInsightsContext();

    useEffect(() => {
        if (userSessionId && !liveStreamCorrelationId) {
            setLiveStreamCorrelationId(`${userSessionId}|${nanoid()}`);
        }
    }, [userSessionId, liveStreamCorrelationId]);

    const currentVideoQuality = useRef<StreamQualityType | undefined>(
        undefined,
    );
    const playerRef = useRef<PlayerRef>(null);
    const playerCardRef = useRef<PlayerCardRef>(null);
    const [canRefresh, setCanRefresh] = useState(false);

    const [isBadNetworkVisible, setIsBadNetworkVisible] = useState(false);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);

    const site = useAppSelector((state) =>
        selectSiteById(state, props.siteId.toString()),
    );

    const device = useAppSelector((state) =>
        selectDeviceById(state, props.deviceId.toString()),
    );

    const [
        createLiveStream,
        { data, isUninitialized, isLoading, isError, error },
    ] = useCreateLiveStreamMutation();

    const allowedLiveStreamFeedTypes = useAppSelector(
        liveStreamingSelectors.selectAllowedLiveStreamFeedTypes,
    );

    // subscribe to SignalR livestreaming updates for the current device
    useSignalRGroupSubscriber(device?.id);

    useEffect(() => {
        // only the first time remember the Hls url
        if (data?.hls && !hlsUrl) {
            setHlsUrl(data.hls);
            dispatch(
                liveStreamingActions.setSelectedDeviceStreamQuality({
                    deviceId: props.deviceId,
                    streamQuality: data.streamQuality,
                }),
            );
        }

        if (isUninitialized) {
            const payload: CreateLiveStreamRequest = {
                deviceId: props.deviceId,
                correlationId: liveStreamCorrelationId,
            };

            if (props.streamQuality) {
                payload.streamQuality = props.streamQuality;
                currentVideoQuality.current = props.streamQuality;
            }

            createLiveStream(payload);
        } else if (
            props.streamQuality &&
            currentVideoQuality.current !== props.streamQuality
        ) {
            //Make player reload source to switch to new quality
            currentVideoQuality.current = props.streamQuality;
            playerRef.current?.reloadStream();
        }
    }, [
        createLiveStream,
        currentVideoQuality,
        data,
        dispatch,
        hlsUrl,
        isUninitialized,
        props.deviceId,
        props.streamQuality,
    ]);

    let videoMouseOverCursor: Property.Cursor | undefined;
    if (document.fullscreenEnabled) {
        videoMouseOverCursor = inWindowMode ? "zoom-in" : "zoom-out";
    }

    const isNonVideoStreamableDevice =
        isError && (error as FetchBaseQueryError)?.status === 422;

    const showIsLoading = !hlsUrl && isLoading;
    const isSuccessfullyInitialized = !showIsLoading && !isError;

    // stream anonimyzation
    const extraRequestHeaders = useMemo(() => {
        // send extra request headers to select live stream feed type
        return new Map<string, string>([["FeedType", liveStreamFeedType]]);
    }, [liveStreamFeedType]);

    return (
        <>
            {site && device && (
                <PlayerCard
                    ref={playerCardRef}
                    alertMessage={
                        isNonVideoStreamableDevice
                            ? "Unsupported camera configuration detected"
                            : undefined
                    }
                    alertSeverity="warning"
                    marginFullScreenTop={48}
                    cardActions={
                        <>
                            <Box
                                sx={{
                                    position: "absolute",
                                    left: 0,
                                    height: "100%",
                                    display: "flex",
                                    flexDirection: "row",
                                    alignItems: "center",
                                }}
                            >
                                <Tooltip
                                    title={
                                        <>
                                            Model: {device.model}
                                            <br />
                                            Vendor: {device.vendor}
                                            <br />
                                            Device type: {device.deviceType}
                                            <br />
                                            {`Supported formats: ${device.supportedImageFormats?.join(", ") ?? "-"}`}
                                        </>
                                    }
                                >
                                    <InfoIcon sx={{ marginLeft: 2 }} />
                                </Tooltip>
                                {isBadNetworkVisible && (
                                    <WifiOff
                                        sx={{ marginLeft: 2, color: "#ff9800" }}
                                    />
                                )}
                                {canRefresh && (
                                    <Tooltip title="Retry the video feed">
                                        <IconButton
                                            onClick={() => {
                                                if (playerRef.current) {
                                                    playerRef.current.reinitializePlayer();
                                                }

                                                setCanRefresh(false);
                                            }}
                                        >
                                            <RefreshIcon />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </Box>
                            <Box
                                sx={{
                                    flexGrow: 1,
                                    flexShrink: 1,
                                    flexBasis: "auto",
                                    textAlign: "center",
                                }}
                            >
                                <Typography
                                    variant="body2"
                                    color="text.primary"
                                >
                                    {site.id} - {device.name}
                                </Typography>
                            </Box>
                            <Box
                                sx={{
                                    position: "absolute",
                                    right: 0,
                                    height: "100%",
                                    display: "flex",
                                    flexDirection: "row",
                                    alignItems: "center",
                                }}
                            >
                                <FeatureFlagGuard
                                    accessibleWithFeatureFlags={[
                                        AppFeature.Diagnostics,
                                    ]}
                                >
                                    <OpenDeviceDiagnosticsButton
                                        device={device}
                                        diagnosticsCorrelationId={
                                            liveStreamCorrelationId
                                        }
                                    />
                                </FeatureFlagGuard>

                                <LiveStreamFeedTypeButton
                                    availableLiveStreamFeedTypes={
                                        allowedLiveStreamFeedTypes
                                    }
                                    selectedLiveStreamFeedType={
                                        liveStreamFeedType
                                    }
                                    onChange={setLiveStreamFeedType}
                                />

                                <OpenInNewWindowButton
                                    siteId={props.siteId}
                                    deviceId={props.deviceId}
                                />

                                {data?.streamQuality && (
                                    <StreamPopOver
                                        streamQuality={
                                            currentVideoQuality.current
                                        }
                                        streamPlayerMode={playerMode}
                                        onStreamQualityChanged={(
                                            selectedStreamQuality: StreamQualityType,
                                        ) => {
                                            //Call backend to switch quality of livestream
                                            const payload: CreateLiveStreamRequest =
                                                {
                                                    deviceId: props.deviceId,
                                                    streamQuality:
                                                        selectedStreamQuality,
                                                    correlationId:
                                                        liveStreamCorrelationId,
                                                };

                                            createLiveStream(payload);
                                        }}
                                        onPlayerModeChanged={(
                                            selectedPlayerMode: StreamPlayerModeType,
                                        ) => {
                                            setPlayerMode(selectedPlayerMode);
                                        }}
                                    ></StreamPopOver>
                                )}
                                <CloseButton
                                    onClick={() =>
                                        dispatch(
                                            liveStreamingActions.setDeviceSelectionState(
                                                {
                                                    siteId: props.siteId,
                                                    deviceId: device.id,
                                                    selected: false,
                                                },
                                            ),
                                        )
                                    }
                                />
                            </Box>
                        </>
                    }
                >
                    {!isError && showIsLoading && (
                        <PlayerCardStatus mode={"progress"} />
                    )}

                    {isError && (
                        <PlayerCardStatus
                            mode={"status"}
                            statusIcon={
                                <VideocamOffIcon sx={{ fontSize: 150 }} />
                            }
                        />
                    )}

                    {isSuccessfullyInitialized && (
                        <HlsPlayer
                            url={hlsUrl}
                            ref={playerRef}
                            playMode={"autoPlay"}
                            playSpeed={1}
                            extraRequestHeaders={extraRequestHeaders}
                            enableAudio={props.enableAudio}
                            audioEnabledByDefault={props.audioEnabledByDefault}
                            onLoadedData={() => {
                                appInsights.getAppInsights().trackEvent({
                                    name: "HLSPlayerLoaded",
                                    properties: {
                                        siteId: props.siteId.toString(),
                                        deviceId: props.deviceId.toString(),
                                        requestHeaders: {
                                            "diagnostic-correlation-id":
                                                liveStreamCorrelationId,
                                        },
                                    },
                                });
                            }}
                            diagnosticCorrelationId={liveStreamCorrelationId}
                            onClickVideo={() => {
                                if (inWindowMode) {
                                    dispatch(
                                        liveStreamingActions.setFullscreenDeviceId(
                                            {
                                                siteId: props.siteId,
                                                deviceId: device.id,
                                            },
                                        ),
                                    );

                                    dispatch(
                                        coreActions.setUIMode("fullscreen"),
                                    );
                                } else {
                                    dispatch(coreActions.setUIMode("window"));
                                }
                            }}
                            onAttachingFaultOccured={() => {
                                setCanRefresh(true);
                            }}
                            videoMouseOverCursor={videoMouseOverCursor}
                            onStreamQualityErrorCountChange={(count) => {
                                // Show warning if there are more than 1 error
                                if (count > 1) {
                                    if (timeoutRef.current) {
                                        clearTimeout(timeoutRef.current);
                                    }
                                    setIsBadNetworkVisible(true);

                                    // Update state to notify there is a bad connection
                                    dispatch(
                                        liveStreamingActions.setBadInternetConnectionDetected(
                                            {
                                                deviceId: props.deviceId,
                                            },
                                        ),
                                    );
                                } else {
                                    // Hide the warning after 10 seconds
                                    timeoutRef.current = setTimeout(() => {
                                        setIsBadNetworkVisible(false);
                                    }, 10 * 1000);
                                }
                            }}
                            playerMode={playerMode}
                        />
                    )}
                </PlayerCard>
            )}

            {site && !device && (
                <Typography
                    variant="h6"
                    color="error"
                >
                    The camera with id {props.deviceId.toString()} could not be
                    found in club {props.siteId.toString()}. Please contact
                    administrator.
                </Typography>
            )}
        </>
    );
}
